import { useEffect, useRef, useState } from 'react';
import type { MediaPlayer, PlayerError } from 'amazon-ivs-player';
import { Button } from '../ui/button';

interface Props {
  playbackUrl: string;
}

export default function LivePlayer({ playbackUrl }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<MediaPlayer | undefined>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'playing' | 'buffering' | 'ended' | 'blocked' | 'error'>('loading');
  const [error, setError] = useState<string>();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let player: MediaPlayer | undefined;
    let cancelled = false;
    setStatus('loading');
    setError(undefined);

    const init = async () => {
      try {
        const ivs = await import('amazon-ivs-player');
        if (cancelled) return;

        if (!ivs.isPlayerSupported) {
          setStatus('error');
          setError('Live video is not supported in this browser.');
          return;
        }

        player = ivs.create({
          wasmWorker: new URL(
            'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js',
            import.meta.url,
          ).toString(),
          wasmBinary: new URL(
            'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm',
            import.meta.url,
          ).toString(),
        });
        playerRef.current = player;

        const onError = (payload: PlayerError) => {
          setStatus('error');
          setError(payload.message || 'The live stream could not be loaded.');
        };
        const onBlocked = () => setStatus('blocked');
        const onReady = () => setStatus('ready');
        const onPlaying = () => setStatus('playing');
        const onBuffering = () => setStatus('buffering');
        const onEnded = () => setStatus('ended');

        player.addEventListener(ivs.PlayerEventType.ERROR, onError);
        player.addEventListener(ivs.PlayerEventType.AUDIO_BLOCKED, onBlocked);
        player.addEventListener(ivs.PlayerEventType.PLAYBACK_BLOCKED, onBlocked);
        player.addEventListener(ivs.PlayerState.READY, onReady);
        player.addEventListener(ivs.PlayerState.PLAYING, onPlaying);
        player.addEventListener(ivs.PlayerState.BUFFERING, onBuffering);
        player.addEventListener(ivs.PlayerState.ENDED, onEnded);

        video.muted = true;
        player.attachHTMLVideoElement(video);
        player.setAutoplay(true);
        player.setMuted(true);
        player.setLiveLowLatencyEnabled(true);
        player.setRebufferToLive(true);
        player.setInitialBufferDuration(1);
        player.load(playbackUrl);
        player.play();
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Live video could not start.');
      }
    };

    void init();

    return () => {
      cancelled = true;
      playerRef.current = undefined;
      player?.delete();
    };
  }, [playbackUrl]);

  const play = () => {
    const promise = videoRef.current?.play();
    void promise?.catch(() => setStatus('blocked'));
    playerRef.current?.play();
  };

  const unmute = () => {
    const video = videoRef.current;
    if (video) video.muted = false;
    playerRef.current?.setMuted(false);
  };

  const showOverlay = status !== 'playing';

  return (
    <div className="relative overflow-hidden rounded-lg bg-black">
      <video
        ref={videoRef}
        controls
        playsInline
        muted
        className="aspect-video w-full bg-black"
      />
      {status === 'playing' ? (
        <Button type="button" plain className="absolute right-3 top-3 bg-black/65 text-white" onClick={unmute}>
          Unmute
        </Button>
      ) : null}
      {showOverlay ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35 p-4 text-center">
          <div className="pointer-events-auto max-w-sm rounded-lg bg-black/75 p-4 text-white shadow-xl">
            {status === 'error' ? (
              <>
                <div className="font-semibold">Stream unavailable</div>
                <p className="mt-1 text-sm text-zinc-300">{error}</p>
              </>
            ) : status === 'ended' ? (
              <>
                <div className="font-semibold">Game video ended</div>
                <p className="mt-1 text-sm text-zinc-300">The replay will appear when it is ready.</p>
              </>
            ) : status === 'blocked' ? (
              <>
                <div className="font-semibold">Ready to play</div>
                <p className="mt-1 text-sm text-zinc-300">Your browser needs a tap before starting live video.</p>
                <Button type="button" color="dark/zinc" className="mt-3" onClick={play}>
                  Play live
                </Button>
              </>
            ) : (
              <>
                <div className="font-semibold">{status === 'buffering' ? 'Catching up to live' : 'Loading live video'}</div>
                <p className="mt-1 text-sm text-zinc-300">Keeping the video close to real time.</p>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
